import { useCallback, useRef, useState } from "react";
import {
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import { Account } from "@common/types/domains/accounting/account";
import { changeDataAfterAccountChanged } from "@components/accounting/journalEntryDrawer/helpers";
import { CreateCoAAccountModal } from "@components/accounting/chartOfAccounts/CoAAccountModal/CreateCoAAccountModal";
import { Button } from "@design-system/Button/Button";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { PlusIcon } from "@icons";
import { SpreadsheetData } from "@components/spreadsheet/types";

export interface CreateAccountModalParams {
    accountName: string;
    accountType: AccountType;
    accountSubtype: AccountSubtype;
    rowIdx: number;
}

interface UseAccountCreationCtaParams {
    accounts: Account[];
    lines: SpreadsheetData;
    onOpenCreateAccount: (params: CreateAccountModalParams) => void;
}

export function useAccountCreationCta({
    accounts,
    lines,
    onOpenCreateAccount,
}: UseAccountCreationCtaParams) {
    const accountsRef = useRef(accounts);
    accountsRef.current = accounts;

    const linesRef = useRef(lines);
    linesRef.current = lines;

    const openCreateAccountModal = useCallback(
        (search: string, closePopover: () => void, rowIdx: number) => {
            closePopover();

            const selectedAccount = accountsRef.current.find(
                (a) => a.id === linesRef.current[rowIdx]?.accountId,
            );

            onOpenCreateAccount({
                accountName: search,
                accountType: (selectedAccount?.type ??
                    AccountType.operatingExpenses) as AccountType,
                accountSubtype: (selectedAccount?.subtype ??
                    AccountSubtype.otherOperatingExpenses) as AccountSubtype,
                rowIdx,
            });
        },
        [onOpenCreateAccount],
    );

    const accountCta = useCallback(
        (search: string, closePopover: () => void, rowIdx: number) => (
            <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                    openCreateAccountModal(search, closePopover, rowIdx)
                }
                leftIcon={<PlusIcon />}
            >
                <BreakableText
                    text={search ? `Create "${search}"` : "Create new account"}
                />
            </Button>
        ),
        [openCreateAccountModal],
    );

    return { accountCta };
}

interface UseCreateAccountModalParams {
    formEntityId: number;
    lines: SpreadsheetData;
    memo: string;
    setFieldValue: (field: string, value: SpreadsheetData) => void;
}

export function useCreateAccountModal({
    formEntityId,
    lines,
    memo,
    setFieldValue,
}: UseCreateAccountModalParams) {
    const [createAccountModal, setCreateAccountModal] = useState<{
        accountName: string;
        accountType: AccountType;
        accountSubtype: AccountSubtype;
    } | null>(null);

    const pendingAccountRowRef = useRef<number | null>(null);

    const linesRef = useRef(lines);
    linesRef.current = lines;

    const memoRef = useRef(memo);
    memoRef.current = memo;

    const onAccountsCreated = useCallback(
        (newAccounts: Account[]) => {
            const rowIdx = pendingAccountRowRef.current;
            if (rowIdx === null) {
                return;
            }

            const matchingAccount = newAccounts.find(
                (a) => "entityId" in a && a.entityId === formEntityId,
            );
            if (!matchingAccount) {
                return;
            }

            const currentLines = [...linesRef.current];
            if (rowIdx >= currentLines.length) {
                pendingAccountRowRef.current = null;
                return;
            }
            currentLines[rowIdx] = {
                ...currentLines[rowIdx],
                accountId: matchingAccount.id,
            };

            const updatedLines = changeDataAfterAccountChanged(
                currentLines,
                rowIdx,
                memoRef.current,
            );
            setFieldValue("journalEntry.lines", updatedLines);

            pendingAccountRowRef.current = null;
        },
        [formEntityId, setFieldValue],
    );

    const openCreateAccountModal = useCallback(
        (params: CreateAccountModalParams) => {
            pendingAccountRowRef.current = params.rowIdx;
            setCreateAccountModal({
                accountName: params.accountName,
                accountType: params.accountType,
                accountSubtype: params.accountSubtype,
            });
        },
        [],
    );

    const createAccountModalElement = createAccountModal && (
        <CreateCoAAccountModal
            show
            onHide={() => setCreateAccountModal(null)}
            entityIds={[formEntityId]}
            accountName={createAccountModal.accountName}
            accountType={createAccountModal.accountType}
            accountSubtype={createAccountModal.accountSubtype}
            onAccountsCreated={onAccountsCreated}
        />
    );

    return { openCreateAccountModal, createAccountModalElement };
}
